// Client HTTP unique vers l'Edge Function `domi-factures`.
// Tout passe par ici : un seul endroit où vivent le jeton, le délai d'attente et
// la politique de reconnexion.

import { jeton, relancerConnexion } from "./etikid.js";

const FN_URL = "https://shuvawbdvfjxohuxvmhp.supabase.co/functions/v1/domi-factures";

/**
 * Délai d'attente. L'original n'en avait AUCUN : une requête pendue laissait la
 * zone sur « Chargement… » indéfiniment, sans annulation possible et sans que
 * l'utilisateur puisse rien faire d'autre que recharger la page.
 */
const TIMEOUT_MS = 20_000;

export class ErreurApi extends Error {
  constructor(message, { statut, expiree = false } = {}) {
    super(message);
    this.name = "ErreurApi";
    this.statut = statut;
    this.expiree = expiree;
  }
}

/**
 * Appelle une action de l'EF.
 * `signal` permet à l'appelant d'annuler (voir `dernierGagne` plus bas).
 */
export async function api(action, params = {}, { signal } = {}) {
  const ctrl = new AbortController();
  const minuteur = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  if (signal) signal.addEventListener("abort", () => ctrl.abort(), { once: true });

  let r;
  try {
    r = await fetch(FN_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + jeton() },
      body: JSON.stringify({ action, ...params }),
      signal: ctrl.signal,
    });
  } catch (e) {
    if (e && e.name === "AbortError") {
      // Annulation volontaire (filtre changé) : l'appelant l'ignore.
      // Expiration du délai : message actionnable, pas « Failed to fetch ».
      if (signal && signal.aborted) throw e;
      throw new ErreurApi("Le serveur met trop de temps à répondre. Réessayez.");
    }
    throw new ErreurApi("Connexion impossible. Vérifiez votre réseau.");
  } finally {
    clearTimeout(minuteur);
  }

  if (r.status === 401) {
    // Le verrou d'etikid.js garantit qu'une seule reconnexion part, même si
    // plusieurs appels échouent en même temps (B3).
    relancerConnexion();
    throw new ErreurApi("Session expirée", { statut: 401, expiree: true });
  }

  if (!r.ok) {
    // On ne relaie PAS le message du serveur tel quel : l'EF renvoie aujourd'hui
    // les erreurs Postgres brutes (noms de tables, de contraintes, fragments de
    // requête). Ça n'aide pas l'utilisateur et ça expose l'architecture interne.
    const detail = await r.json().catch(() => ({}));
    console.error("[api] échec", action, r.status, detail); // visible au débogage seulement
    throw new ErreurApi("Le serveur n'a pas pu traiter la demande.", { statut: r.status });
  }

  return r.json();
}

/**
 * Fabrique un appelant « le dernier gagne » : chaque nouvel appel annule le
 * précédent.
 *
 * CORRIGÉ (B8) — sans ça, deux requêtes lancées à la suite (frappe rapide dans
 * un filtre) reviennent dans un ordre non garanti, et la PREMIÈRE revenue en
 * dernier écrase le résultat de la seconde. Le tableau affiche alors la réponse
 * d'un filtre que l'utilisateur a déjà remplacé — un décalage silencieux entre
 * ce qui est écrit dans le champ et ce qui est montré en dessous.
 *
 * Un appelant par zone : les zones ne doivent pas s'annuler entre elles.
 */
export function dernierGagne() {
  let ctrl = null;
  return async function appeler(action, params = {}) {
    if (ctrl) ctrl.abort();
    ctrl = new AbortController();
    const mien = ctrl;
    const res = await api(action, params, { signal: mien.signal });
    if (mien !== ctrl) throw new ErreurApi("annulé", { statut: 0 }); // devancé entre-temps
    ctrl = null;
    return res;
  };
}

/** Vrai si l'erreur vient d'une annulation volontaire (à ignorer silencieusement). */
export function estAnnulation(e) {
  return Boolean(e) && (e.name === "AbortError" || (e instanceof ErreurApi && e.statut === 0));
}
